<template>
  <div
    class="isinCard card"
    :class="isDarkMode ? 'isin-card-dark' : 'isin-card-light'"
    :style="isDarkMode
      ? 'background: #0F172A !important; background-color: #0F172A !important; border: 1px solid #334155 !important; box-shadow: none !important; color: #E2E8F0;'
      : ''"
    @click="onISINInfoCardClick"
  >
    <div
      class="px-3 py-2 card-body"
      :style="isDarkMode ? 'background: transparent !important;' : ''"
    >
      <div class="d-flex align-items-center gap-2 mb-1">
        <div
          class="d-flex align-items-center justify-content-center flex-shrink-0 isin-logo"
          :class="{ 'isin-logo-fallback': !showShareLogo }"
        >
          <img
            v-if="showShareLogo"
            :src="shareLogoUrl(isin)"
            :alt="title"
            @error="onLogoError"
          />
          <span v-else>{{ shareMonogram(title) }}</span>
        </div>
        <div class="min-w-0 flex-grow-1">
          <p class="mb-0 isin-title text-truncate">{{ title }}</p>
          <span v-if="isin" class="isin-code font-monospace d-block text-truncate">{{ isin }}</span>
        </div>
        <button
          v-if="id"
          type="button"
          class="forum-bubble-btn"
          :class="{ 'forum-bubble-btn-dark': isDarkMode }"
          :title="'Fórum: ' + title"
          @click.stop="openShareForum"
        >
          <fa-icon icon="fa-regular fa-comment-dots" />
        </button>
      </div>

      <div class="d-flex align-items-center justify-content-between gap-2">
        <div class="d-flex align-items-center gap-3 min-w-0 flex-grow-1" :class="contentClass">
          <h5 class="font-weight-bolder isin-amount mb-0">
            {{ formattedPrice }}
            <span class="isin-currency">{{ currencyLabel }}</span>
          </h5>
          <div class="isin-change">
            <div class="isin-change-row">
              <span :class="priceClass">{{ formatChangeTypeNumber(amtDiff) }} {{ currencyLabel }}</span>
              <span class="isin-pct">({{ formatChangeTypeNumber(pctDiff) }}%)</span>
            </div>
            <span v-if="againstLabel" class="isin-date">{{ againstLabel }}</span>
          </div>
        </div>
        <div class="isin-spark flex-shrink-0" aria-hidden="true">
          <svg v-if="iconChangeType == 1" width="52" height="18" viewBox="0 0 50 20" fill="none">
            <path d="M2 16C10 15 15 10 23 9C31 8 38 5 48 3" stroke="#15803D" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <svg v-else-if="iconChangeType == -1" width="52" height="18" viewBox="0 0 50 20" fill="none">
            <path d="M2 4 C10 5 15 10 23 11C31 12 38 15 48 17" stroke="#B91C1C" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <svg v-else width="52" height="18" viewBox="0 0 50 20" fill="none">
            <path d="M2 10 C10 12 18 6 25 10C32 14 40 8 48 10" :stroke="isDarkMode ? '#64748B' : '#CBD5E1'" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive } from "vue";
import { useStore } from "vuex";
import { shareLogoUrl, shareMonogram } from "@/helpers/shareLogos";
import { formatTrimZeroCents } from "@/utils/NumberFormat";

const props = defineProps({
  id: {
    type: Number
  },
  title: {
    type: String
  },
  isin: {
    type: String,
    default: ""
  },
  currency: {
    type: String
  },
  price: {
    type: [Number, String],
    default: null
  },
  amtDiff: {
    type: String
  },
  pctDiff: {
    type: String
  },
  priceChangeType: {
    type: Number
  },
  iconChangeType: {
    type: Number
  },
  lastDate: {
    type: String
  },
  contentClass: {
    type: String
  }
});

const store = useStore();
const isDarkMode = computed(() => !!store.state.isDarkMode);
const logoFailed = reactive({});

const showShareLogo = computed(() => !!(shareLogoUrl(props.isin) && !logoFailed[props.isin]));

const priceClass = computed(() => ({
  "text-success": props.priceChangeType == 1,
  "text-info": props.priceChangeType == 0,
  "text-danger": props.priceChangeType == -1
}));

const formattedPrice = computed(() => formatTrimZeroCents(props.price) || "—");

const currencyLabel = computed(() => {
  const code = String(props.currency || "").toUpperCase();
  return code === "CZK" ? "Kč" : (props.currency || "Kč");
});

const CZECH_DAYS = ["Ne", "Po", "Út", "St", "Čt", "Pá", "So"];

const parseLastDate = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  let match = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  match = raw.match(/(\d{1,2})\.(\d{1,2})\.?(\d{2,4})?/);
  if (match) {
    const year = match[3]
      ? (match[3].length === 2 ? 2000 + Number(match[3]) : Number(match[3]))
      : new Date().getFullYear();
    return new Date(year, Number(match[2]) - 1, Number(match[1]));
  }
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const againstLabel = computed(() => {
  const date = parseLastDate(props.lastDate);
  if (date) {
    return `ZMĚNA PROTI ${CZECH_DAYS[date.getDay()]} ${date.getDate()}.${date.getMonth() + 1}.`;
  }
  return props.lastDate ? `ZMĚNA PROTI ${props.lastDate}` : "";
});

const formatChangeTypeNumber = (value) => {
  return store.getters.formatChangeTypeNumber(value);
};

const onLogoError = () => {
  if (props.isin) logoFailed[props.isin] = true;
};

const onISINInfoCardClick = () => {
  store.dispatch("fetchISINChartData", {
    ISINID: props.id,
    ISIN: props.isin,
    Nm: props.title
  });
};

const openShareForum = () => {
  if (!props.id) return;
  store.dispatch("openForum", {
    title: props.title || "Akcie",
    ISINID: props.id,
    ISIN: props.isin
  });
};
</script>

<style scoped lang="scss">
.isinCard {
  border-radius: 1rem !important;
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    cursor: pointer;
    transform: translateY(-3px);
  }
}

.isin-card-light {
  background: #ffffff;
  border: 1px solid #E2E8F0 !important;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);

  &:hover {
    box-shadow: 0 15px 30px rgba(15, 23, 42, 0.1);
  }
}

.isin-card-dark {
  background: #0F172A !important;
  border: 1px solid #334155 !important;
  box-shadow: none !important;
  color: #E2E8F0;

  &:hover {
    border-color: rgba(255, 122, 0, 0.4) !important;
    box-shadow: 0 12px 24px rgba(255, 77, 0, 0.12) !important;
  }

  .isin-title {
    color: #E2E8F0 !important;
  }

  .isin-amount {
    color: #E2E8F0 !important;
  }

  .isin-date,
  .isin-code,
  .isin-pct {
    color: #94A3B8 !important;
  }
}

.isin-logo {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
}

.isin-logo img {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.isin-logo-fallback {
  background: rgba(100, 116, 139, 0.16);
  color: #64748b;
  font-size: 0.68rem;
  font-weight: 700;
}

.isin-title {
  font-size: 0.82rem;
  font-weight: 800;
  line-height: 1.2;
  color: #0F172A;
  text-transform: none;
}

.isin-code {
  font-size: 0.68rem;
  color: #64748B;
  margin-top: 1px;
}

.isin-amount {
  font-size: 1.42rem;
  letter-spacing: -0.03em;
  line-height: 1.1;
  white-space: nowrap;
}

.isin-currency {
  font-size: 0.78rem;
  font-weight: 500;
  letter-spacing: 0;
  opacity: 0.7;
}

.isin-change {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
  min-width: 0;
}

.isin-change-row {
  display: flex;
  align-items: baseline;
  gap: 0.45rem;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
}

.isin-pct {
  font-weight: 500;
  color: #64748B;
}

.isin-date {
  font-size: 0.56rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  margin-top: 1px;
}

.isin-spark {
  line-height: 0;
  padding-bottom: 2px;
}

.forum-bubble-btn {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1.5px solid #CBD5E1;
  background: transparent;
  color: #64748B;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 0.75rem;
}

.forum-bubble-btn:hover {
  color: #FF8A4C;
  border-color: #FF8A4C;
  background: rgba(255, 138, 76, 0.08);
}

.forum-bubble-btn-dark {
  border-color: #475569;
  color: #94A3B8;
}

.forum-bubble-btn-dark:hover {
  color: #FF8A4C;
  border-color: #FF8A4C;
  background: rgba(255, 138, 76, 0.14);
}
</style>

<style>
body.inga-theme-dark .isinCard.card,
body.inga-theme-dark .isinCard .card-body {
  background: #0F172A !important;
  background-color: #0F172A !important;
  color: #E2E8F0 !important;
}
body.inga-theme-dark .isinCard.card {
  border: 1px solid #334155 !important;
  box-shadow: none !important;
}
body.inga-theme-dark .isinCard .isin-title {
  color: #E2E8F0 !important;
}
body.inga-theme-dark .isinCard .isin-date,
body.inga-theme-dark .isinCard .isin-code,
body.inga-theme-dark .isinCard .isin-pct {
  color: #94A3B8 !important;
}
body.inga-theme-dark .isinCard .isin-amount {
  color: #E2E8F0 !important;
}
</style>
